import React, { useEffect } from 'react';
import _ from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';
import PpiForm from '../components/PpiForm';
import ProgressFullscreen from '../components/common/ProgressFullscreen';
import useExcludedIngredients from '../features/IngredientContainer/hooks/useExcludedIngredients';
import useIngredients from '../features/IngredientContainer/hooks/useIngredients';
import useCreatePpi from '../features/PpiContainer/hooks/useCreatePpi';
import { getPpiDefaultData } from '../features/PpiContainer/util';
import { ppiInProgressRoute, ppiPublishedRoute } from '../routes';
import { getAvailableLanguages, getLanguage } from '../features/app/selectors';
import usePpi from '../features/PpiContainer/hooks/usePpi';
import useQueryParams from '../utils/useQueryParams';
import copyPpi from '../features/PpiContainer/copy';
import copyPublishedPpi from 'features/PpiContainer/copyPublishedPpi';
import { MARKET_GLOBAL_LANG } from '../features/app/constants';
import { Mode } from '../components/PpiForm/constants';
import View from './views';
import Splash from '../components/Splash';

import { getStockHierarchy } from '../features/app/stockHierarchySelectors';
import { createPpiVariant } from 'features/PpiContainer/actions';
import {
  getvaraintPpi,
  getfetchingVariantPpi,
  getfetchVariantPpiError
} from 'features/PpiContainer/selectors';
// import { getGlobalPpi, getfetchingGlobalPpi, getGlobalPpiError } from 'features/PpiContainer/selectors';
// Hard-coded for now, could potentially be passed and query string parameter
const copyFromLanguage = MARKET_GLOBAL_LANG;

function getIngredient(...ingredientLists) {
  let ingredient = null;
  ingredientLists.forEach(ingredientList => {
    if (ingredientList.size > 0) {
      ingredient = ingredientList.first().toJS();
    }
  });
  return ingredient;
}

/**
 * Clears the presentation name of the data. This allows the default name to
 * used rather than the name in english.
 * @param data
 * @returns {*}
 */
function clearPresentationName(data) {
  const dataWithoutName = _.cloneDeep(data);
  _.keys(data.info).forEach(language => {
    delete dataWithoutName.info[language].presentationName;
  });
  return dataWithoutName;
}

// This only works when copying a Global PPI. It copies EN-IF into all languages for the user.
const PpiCreateFromExistingView = () => {
  const dispatch = useDispatch();
  // Need both id of PPI and enrichment to create PPI from in order to be able
  // to reload the view without breaking it (can't fetch PPI based on enrichment
  // id).
  const [fromPpiId, fromEnrichmentId, itemCode, localPpiCopy] = useQueryParams(
    'fromPpiId',
    'fromEnrichmentId',
    'itemCode',
    'localPpiCopy'
  );
  const [fromPpi, isFetchingPpi] = usePpi(fromPpiId);
  const [createPpi, isSaving, createdPpiId] = useCreatePpi();
  const languages = useSelector(getAvailableLanguages);
  // Currently the ingredients, excluded ingredients and global ingredients is
  // served by three endpoints. Each endpoint has one endpoint and there is no
  // way to know in which category it is. Hence three hooks.
  const [isFetchingIngredients, ingredientRaw] = useIngredients({ itemCode });
  const [isFetchingExcluded, excludedIngredientsRaw] = useExcludedIngredients({
    itemCode
  });
  // const [
  //   isFetchingGlobalIngredients,
  //   globalIngredientRaw
  // ] = useGlobalIngredients({ itemCode });
  const ingredient = getIngredient(
    ingredientRaw,
    excludedIngredientsRaw
    // globalIngredientRaw
  );
  const ppi = useSelector(getvaraintPpi);
  // const globalPpi = useSelector(getGlobalPpi);
  // const fetchGlobalError = useSelector(getGlobalPpiError);
  // const isFecthingGlobal = useSelector(getfetchingGlobalPpi);
  const fetchvaraintError = useSelector(getfetchVariantPpiError);
  const isFecthingVariant = useSelector(getfetchingVariantPpi);
  const currLanguage = useSelector(getLanguage);
  const firstStorageFromConfig = useSelector(getStockHierarchy).toJS();
  const isFetching =
    isFetchingPpi ||
    isFetchingIngredients ||
    isFecthingVariant ||
    isFetchingExcluded;

  useEffect(() => {
    if (
      localPpiCopy === 'DRAFT' ||
      localPpiCopy === 'PUBLISHED' ||
      localPpiCopy === 'UNPUBLISHED'
    ) {
      dispatch(createPpiVariant(fromEnrichmentId, localPpiCopy));
    }
  }, []);

  // Redirect, if created
  if (createdPpiId) {
    return <Redirect to={ppiInProgressRoute(createdPpiId)} />;
  }

  // Render spinner
  if (isFetching) {
    return <ProgressFullscreen open />;
  }

  if (fetchvaraintError) {
    if (localPpiCopy === 'DRAFT') {
      return <Redirect to={ppiInProgressRoute(fromEnrichmentId)} />;
    }
    if (localPpiCopy === 'PUBLISHED' || localPpiCopy === 'UNPUBLISHED') {
      return <Redirect to={ppiPublishedRoute(fromEnrichmentId)} />;
    }
  }

  if (!ingredient) {
    return (
      <Splash content="Sorry! The product you are looking for doesn’t exist. This issue must be solved in iFood, please contact the Range Support team (Food.RangeSupport.gl@inter.ikea.com)." />
    );
  }

  function handleCreatePpi(data, callback) {
    if (
      (fromPpiId &&
        (localPpiCopy === 'undefined' ||
          localPpiCopy === 'PUBLISHED' ||
          localPpiCopy === 'DRAFT')) ||
      localPpiCopy === 'UNPUBLISHED'
    ) {
      data.parentPpiId = fromPpiId;
    } else {
      data.parentPpiId = null;
    }
    if (fromPpiId && localPpiCopy === 'false') {
      data.parentPpiEnrichmentDataId = null;
    }
    createPpi(data, callback);
  }

  function getVariantName(data, ppi, toLanguages, status) {
    let copyName;
    if (status === 'DRAFT') {
      copyName = ppi.inProgressPPIEnrichmentData;
    } else if (status === 'PUBLISHED' || status === 'UNPUBLISHED') {
      copyName = ppi.publishedPPIEnrichmentData;
    }
    const result = { ...data };
    toLanguages.forEach(toLanguage => {
      result.info[toLanguage].presentationName =
        copyName.info[toLanguage].presentationName;
    });
    return result;
  }

  let id;
  if (
    localPpiCopy === 'DRAFT' ||
    localPpiCopy === 'PUBLISHED' ||
    localPpiCopy === 'UNPUBLISHED'
  ) {
    id = fromPpi.publishedPPIEnrichmentData.id;
  } else {
    id = fromEnrichmentId;
  }

  let data =
    localPpiCopy === 'true'
      ? copyPublishedPpi(fromPpi, fromEnrichmentId, languages)
      : copyPpi(fromPpi, id, copyFromLanguage, languages);

  data =
    localPpiCopy === 'DRAFT' ||
    localPpiCopy === 'PUBLISHED' ||
    localPpiCopy === 'UNPUBLISHED'
      ? getVariantName(data, ppi, languages, localPpiCopy)
      : clearPresentationName(data);

  const defaultData = getPpiDefaultData(
    languages,
    ingredient,
    firstStorageFromConfig,
    currLanguage
  );
  View.PpiCreateFromExisting.visit(ingredient);

  return (
    <PpiForm
      data={data}
      defaultData={defaultData}
      isSaving={isSaving}
      dataLabels={{
        title: (formData, { language }) =>
          _.get(formData, `info.${language}.presentationName`)
      }}
      mode={Mode.CREATE}
      onSave={handleCreatePpi}
    />
  );
};

export default PpiCreateFromExistingView;
